import {
  AdminAuditListResponseSchema,
  AdminCreateTenantResponseSchema,
  AdminDeleteTenantSecretResponseSchema,
  AdminImpersonationListResponseSchema,
  AdminPutTenantSecretResponseSchema,
  AdminTenantDetailResponseSchema,
  AdminTenantSecretsResponseSchema,
  AdminTenantsListResponseSchema,
  RevokeImpersonationResponseSchema,
  StartImpersonationResponseSchema,
  type AdminAuditListResponse,
  type AdminCreateTenantBody,
  type AdminCreateTenantResponse,
  type AdminDeleteTenantSecretResponse,
  type AdminImpersonationListResponse,
  type AdminPutTenantSecretResponse,
  type AdminTenantDetailResponse,
  type AdminTenantSecretsResponse,
  type AdminTenantsListResponse,
  type RevokeImpersonationResponse,
  type StartImpersonationBody,
  type StartImpersonationResponse,
  type TenantSecretService,
} from '../../src/shared/admin';
import {
  AdminAttachPhoneNumberResponseSchema,
  AdminPhoneNumbersResponseSchema,
  AdminReleasePhoneNumberResponseSchema,
  type AdminAttachPhoneNumberBody,
  type AdminAttachPhoneNumberResponse,
  type AdminPhoneNumbersResponse,
  type AdminReleasePhoneNumberResponse,
} from '../../src/shared/numbers';
import { apiDelete, apiGet, apiPost, apiPut } from './client';

/**
 * Typed SPA client for the platform-admin endpoints (ARCHITECTURE_PLAN §6.4,
 * §9 `/api/admin/*`, §10 `/admin`). Schemas are the single source of truth
 * from `src/shared/admin.ts` (§3). Responses are validated on arrival so a
 * contract drift between Worker and SPA fails loudly.
 */

export const adminKeys = {
  tenants: ['admin', 'tenants'] as const,
  tenant: (id: string) => ['admin', 'tenants', id] as const,
  tenantAudit: (id: string, cursor?: string) =>
    ['admin', 'tenants', id, 'audit', cursor ?? ''] as const,
  tenantSecrets: (id: string) => ['admin', 'tenants', id, 'secrets'] as const,
  tenantNumbers: (id: string) => ['admin', 'tenants', id, 'numbers'] as const,
  impersonations: ['admin', 'impersonations'] as const,
};

/** `POST /api/admin/tenants` — super-user tenant provisioning (owner + company + account). */
export function createAdminTenant(body: AdminCreateTenantBody): Promise<AdminCreateTenantResponse> {
  return apiPost('/api/admin/tenants', body, AdminCreateTenantResponseSchema);
}

/** `GET /api/admin/tenants/:id/secrets` — masked per-tenant provider secrets. */
export function fetchTenantSecrets(
  id: string,
  signal?: AbortSignal
): Promise<AdminTenantSecretsResponse> {
  return apiGet(
    `/api/admin/tenants/${encodeURIComponent(id)}/secrets`,
    AdminTenantSecretsResponseSchema,
    signal
  );
}

/** `PUT /api/admin/tenants/:id/secrets/:service` — set/replace one provider secret. */
export function putTenantSecret(
  id: string,
  service: TenantSecretService,
  value: string
): Promise<AdminPutTenantSecretResponse> {
  return apiPut(
    `/api/admin/tenants/${encodeURIComponent(id)}/secrets/${encodeURIComponent(service)}`,
    { value },
    AdminPutTenantSecretResponseSchema
  );
}

/** `DELETE /api/admin/tenants/:id/secrets/:service` — remove one provider secret. */
export function deleteTenantSecret(
  id: string,
  service: TenantSecretService
): Promise<AdminDeleteTenantSecretResponse> {
  return apiDelete(
    `/api/admin/tenants/${encodeURIComponent(id)}/secrets/${encodeURIComponent(service)}`,
    AdminDeleteTenantSecretResponseSchema
  );
}

/** `GET /api/admin/tenants/:id/numbers` — a tenant's phone numbers. */
export function listTenantNumbers(
  id: string,
  signal?: AbortSignal
): Promise<AdminPhoneNumbersResponse> {
  return apiGet(
    `/api/admin/tenants/${encodeURIComponent(id)}/numbers`,
    AdminPhoneNumbersResponseSchema,
    signal
  );
}

/** `POST /api/admin/tenants/:id/numbers` — attach a Twilio number the operator already owns. */
export function attachTenantNumber(
  id: string,
  body: AdminAttachPhoneNumberBody
): Promise<AdminAttachPhoneNumberResponse> {
  return apiPost(
    `/api/admin/tenants/${encodeURIComponent(id)}/numbers`,
    body,
    AdminAttachPhoneNumberResponseSchema
  );
}

/** Retry provider setup on an existing number without inserting another row. */
export function configureTenantNumberWebhooks(
  id: string,
  numberId: string
): Promise<AdminAttachPhoneNumberResponse> {
  return apiPost(
    `/api/admin/tenants/${encodeURIComponent(id)}/numbers/${encodeURIComponent(numberId)}/webhooks`,
    {},
    AdminAttachPhoneNumberResponseSchema
  );
}

/** `DELETE /api/admin/tenants/:id/numbers/:numberId` — release a number (status flip, not a delete). */
export function releaseTenantNumber(
  id: string,
  numberId: string
): Promise<AdminReleasePhoneNumberResponse> {
  return apiDelete(
    `/api/admin/tenants/${encodeURIComponent(id)}/numbers/${encodeURIComponent(numberId)}`,
    AdminReleasePhoneNumberResponseSchema
  );
}

/** `GET /api/admin/tenants` — list tenants (keyset-paginated). */
export function fetchAdminTenants(
  cursor?: string,
  signal?: AbortSignal
): Promise<AdminTenantsListResponse> {
  const path =
    cursor === undefined
      ? '/api/admin/tenants'
      : `/api/admin/tenants?cursor=${encodeURIComponent(cursor)}`;
  return apiGet(path, AdminTenantsListResponseSchema, signal);
}

/** `GET /api/admin/tenants/:id` — tenant detail. */
export function fetchAdminTenant(
  id: string,
  signal?: AbortSignal
): Promise<AdminTenantDetailResponse> {
  return apiGet(
    `/api/admin/tenants/${encodeURIComponent(id)}`,
    AdminTenantDetailResponseSchema,
    signal
  );
}

/** `GET /api/admin/tenants/:id/audit` — a tenant's audit log (keyset-paginated). */
export function fetchAdminTenantAudit(
  id: string,
  cursor?: string,
  signal?: AbortSignal
): Promise<AdminAuditListResponse> {
  const path =
    cursor === undefined
      ? `/api/admin/tenants/${encodeURIComponent(id)}/audit`
      : `/api/admin/tenants/${encodeURIComponent(id)}/audit?cursor=${encodeURIComponent(cursor)}`;
  return apiGet(path, AdminAuditListResponseSchema, signal);
}

/** `GET /api/admin/impersonations` — list impersonation grants (keyset-paginated). */
export function fetchAdminImpersonations(
  cursor?: string,
  signal?: AbortSignal
): Promise<AdminImpersonationListResponse> {
  const path =
    cursor === undefined
      ? '/api/admin/impersonations'
      : `/api/admin/impersonations?cursor=${encodeURIComponent(cursor)}`;
  return apiGet(path, AdminImpersonationListResponseSchema, signal);
}

/** `POST /api/admin/impersonation` — start an impersonation session. Sets a new session cookie. */
export function startImpersonation(
  body: StartImpersonationBody
): Promise<StartImpersonationResponse> {
  return apiPost('/api/admin/impersonation', body, StartImpersonationResponseSchema);
}

/** `POST /api/admin/impersonation/:grantId/revoke` — revoke a grant immediately. */
export function revokeImpersonation(grantId: string): Promise<RevokeImpersonationResponse> {
  return apiPost(
    `/api/admin/impersonation/${encodeURIComponent(grantId)}/revoke`,
    {},
    RevokeImpersonationResponseSchema
  );
}

/** Resolve zod schemas for local parse use. */
export const AdminSchemas = {
  AdminTenantsListResponseSchema,
  AdminTenantDetailResponseSchema,
  AdminAuditListResponseSchema,
  AdminImpersonationListResponseSchema,
};
